import { TabSwitch, TabSwitchTypes, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation } from '@react-navigation/native';
import { Box } from 'native-base';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../hooks/useToastCreator';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../routes/types';
import { useProductManagementActions } from '../../../../store/ProducManagement/actions';
import { modalSelectedItemsState } from '../../../../store/ProducManagement/atom';
import { ProductsManagementDetailQuery } from '../../../../store/ProducManagement/selector';
import {
  DeleteControlProductPayload,
  InsertControlProductPayload,
} from '../../../../store/ProducManagement/types';
import { ModalIncludeExcludeBlock } from '../../../ProductsManagement/components/ModalIncludeExcludeBlock';
import { ProductType } from '../../../ProductsManagement/constants';
import {
  CreateBlockModelContent,
  DeleteBlockModelContent,
  formatResponseDataPerTab,
} from '../../constants';
import { BlockedResourceList } from '../BlockedResourceList';
import { ProductInfo } from '../productInfo';

export interface DetailSectionProps {
  routeParams: RouteProp<SignedInRootStackParamList, 'ProductsManagementDetails'>;
}
export const DetailSection = ({ routeParams }: DetailSectionProps) => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast } = useToastCreator('toast-products-management-details');

  const [selectedTab, setSelectedTab] = useState('partners');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemToRemoveId, setItemToRemoveId] = useState<number | null>(null);

  const {
    productId,
    productFilterIdAction,
    productTypeId,
    productName,
    managerImage,
    productTypeName,
  } = routeParams.params;

  const queryParams = useMemo(
    () => ({
      productId,
      productFilterIdAction,
      productTypeId,
    }),
    [productId, productFilterIdAction, productTypeId]
  );

  const { data, loading, error, refresher } = useRecoilQuery(
    ProductsManagementDetailQuery(queryParams),
    {
      cachePolicy: 'no-cache',
    }
  );
  const { insertFixedIncomeControlProduct, insertControlProduct, deleteControlProduct } =
    useProductManagementActions();
  const [modalSelectedItems, setModalSelectedItems] = useRecoilState(modalSelectedItemsState);
  const [isLoading, setIsLoading] = useState(false);

  const { advisors, customers, partners } = useMemo(() => {
    return formatResponseDataPerTab(data);
  }, [data]);

  useEffect(() => {
    if (modalSelectedItems?.destinationId) {
      setIsModalOpen(true);
    }
  }, [modalSelectedItems]);

  const handleNewManagementProduct = () => {
    navigate('ProductsManagementBlockModal', {
      productTypeId,
    });
  };

  const handleOpenRemoveModal = (id: number) => {
    setItemToRemoveId(id);
    setIsModalOpen(true);
  };

  const tabs = useMemo(
    () =>
      [
        {
          key: 'partners',
          label: 'Parceiros',
          component: (
            <BlockedResourceList
              title="Parceiros bloqueados"
              data={partners}
              isLoading={loading}
              error={!!error}
              onRefresh={refresher}
              onNewBlock={handleNewManagementProduct}
              onRemoveBlock={handleOpenRemoveModal}
            />
          ),
        },
        {
          key: 'advisors',
          label: 'Assessores',
          component: (
            <BlockedResourceList
              title="Assessores bloqueados"
              data={advisors}
              isLoading={loading}
              error={!!error}
              onRefresh={refresher}
              onNewBlock={handleNewManagementProduct}
              onRemoveBlock={handleOpenRemoveModal}
            />
          ),
        },
        {
          key: 'customers',
          label: 'Clientes',
          component: (
            <BlockedResourceList
              title="Clientes bloqueados"
              data={customers}
              isLoading={loading}
              error={!!error}
              onRefresh={refresher}
              onNewBlock={handleNewManagementProduct}
              onRemoveBlock={handleOpenRemoveModal}
            />
          ),
        },
      ] as const,
    [partners, advisors, customers, error]
  );

  const handleCloseModal = () => {
    if (!isLoading) {
      setIsModalOpen(false);
      setItemToRemoveId(null);
      setModalSelectedItems(null);
    }
  };

  const handleConfirmIncludeExclude = async () => {
    if (itemToRemoveId && !isLoading) {
      setIsLoading(true);

      const payload: DeleteControlProductPayload = {
        informationProductFilterId: itemToRemoveId,
        productTypeId,
      };

      await deleteControlProduct(payload)
        .then(() => {
          refresher();
          setIsModalOpen(false);
          setItemToRemoveId(null);

          showToast('success', 'Tudo certo!', 'Item removido com sucesso!');
        })
        .finally(() => {
          setIsLoading(false);
        });

      return;
    }

    if (modalSelectedItems && !isLoading) {
      setIsLoading(true);

      try {
        const payload: InsertControlProductPayload = {
          productTypeId,
          productId,
          productFilterPlatformId: modalSelectedItems.platformId,
          productFilterDestinationId: modalSelectedItems.destinationId,
          ...(modalSelectedItems.productList?.length && {
            productsList: modalSelectedItems.productList,
          }),
          ...(modalSelectedItems.isClientOrAdvisor === 'client' && {
            accountId: modalSelectedItems.clientOrAdvisorNameId?.id,
          }),
          ...(modalSelectedItems.isClientOrAdvisor === 'advisor' && {
            advisorId: modalSelectedItems.clientOrAdvisorNameId?.id,
          }),
        };

        await (productTypeId === ProductType.FIXED_INCOME
          ? insertFixedIncomeControlProduct(payload)
          : insertControlProduct(payload));

        const message =
          modalSelectedItems.isClientOrAdvisor === 'client'
            ? `Cliente(a) ${modalSelectedItems.clientOrAdvisorNameId?.name} foi bloqueado com sucesso!`
            : modalSelectedItems.isClientOrAdvisor === 'advisor'
            ? `Assessor(a) ${modalSelectedItems.clientOrAdvisorNameId?.name} foi bloqueado com sucesso!`
            : 'O parceiro foi bloqueado com sucesso!';

        showToast('success', 'Tudo certo!', message);
        refresher();
      } finally {
        setIsModalOpen(false);
        setModalSelectedItems(null);
        setIsLoading(false);
      }
    }
  };

  return (
    <Box flex={1} background={{ base: 'transparent', sm: Color['neutral.white'] }}>
      <VStack py={Spacing['stack.xxs']} px={Spacing['inline.xxs']}>
        <ProductInfo
          productName={productName}
          managerImage={managerImage}
          productTypeName={productTypeName}
        />
        <Box py={Spacing['stack.xxs']} flex={1}>
          <TabSwitch
            testId="tab-switch"
            tabs={tabs as unknown as TabSwitchTypes.Props['tabs']}
            selectedTab={selectedTab}
            onTabPress={setSelectedTab as TabSwitchTypes.Props['onTabPress']}
            isMobile={isMobile}
          />
        </Box>
      </VStack>

      <ModalIncludeExcludeBlock
        isModalOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmIncludeExclude}
        isLoading={isLoading}>
        {itemToRemoveId && <DeleteBlockModelContent />}
        {modalSelectedItems?.destinationId && <CreateBlockModelContent {...modalSelectedItems} />}
      </ModalIncludeExcludeBlock>
    </Box>
  );
};
